import React, { useMemo } from "react";
import {
  Alert,
  Button,
  Empty,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import PageHeader from "../../../components/PageHeader";
import { useTableUrlState } from "../../../hooks/useTableUrlState";
import {
  CONTENT_HEALTH_STATUS_ORDER,
  CONTENT_HEALTH_STATUS_TAGS,
} from "../../../constants/status-colors";
import { ContentNodeType } from "../../../services/content-tree/content-tree.types";
import { getEditPath } from "../ContentMap/utils";
import {
  AttentionItem,
  ENTITY_BADGE_COLOR,
  entityBadgeLabel,
  filterAttentionItems,
} from "./utils";
import { useAttentionData } from "./useAttentionData";

const { Text } = Typography;

// Static filter config (identity must survive re-renders — hook contract).
// Selects commit to the URL immediately; there is no free-text filter here.
const TABLE_FILTERS = {
  type: "immediate",
  status: "immediate",
} as const;

const NODE_TYPES: ContentNodeType[] = [
  "program",
  "course",
  "lesson",
  "activity",
  "resource",
  "project",
  "phase",
  "game_quiz",
];

const TYPE_OPTIONS = NODE_TYPES.map((type) => ({
  value: type,
  label: entityBadgeLabel(type),
}));

const STATUS_OPTIONS = CONTENT_HEALTH_STATUS_ORDER.map((status) => ({
  value: status,
  label: CONTENT_HEALTH_STATUS_TAGS[status].mapLabel,
}));

const COLUMNS: ColumnsType<AttentionItem> = [
  {
    title: "Tipo",
    dataIndex: "type",
    key: "type",
    width: 130,
    render: (type: ContentNodeType) => (
      <Tag color={ENTITY_BADGE_COLOR[type] ?? "default"}>
        {entityBadgeLabel(type)}
      </Tag>
    ),
  },
  {
    title: "Contenido",
    dataIndex: "title",
    key: "title",
    render: (_: string, item) => {
      const editPath = getEditPath(item);
      const label = `#${item.id} ${item.title}`;
      // react-router Link renders a real <a href>, so middle-click and
      // Cmd/Ctrl+click open the edit page in a new tab natively.
      return editPath ? <Link to={editPath}>{label}</Link> : <Text>{label}</Text>;
    },
  },
  {
    title: "Problema",
    dataIndex: "status",
    key: "status",
    width: 160,
    render: (_: string, item) => {
      const tag = (
        <Tag color={CONTENT_HEALTH_STATUS_TAGS[item.status].color}>
          {CONTENT_HEALTH_STATUS_TAGS[item.status].mapLabel}
        </Tag>
      );
      return item.statusDetail ? (
        <Tooltip title={item.statusDetail}>{tag}</Tooltip>
      ) : (
        tag
      );
    },
  },
  {
    title: "Ubicación",
    dataIndex: "parentPath",
    key: "parentPath",
    render: (parentPath: string) => (
      <Text type="secondary">{parentPath || "—"}</Text>
    ),
  },
];

const AttentionQueue: React.FC = () => {
  const { items, loading, error, generatedAt, incompletePrograms, refetch } =
    useAttentionData();
  const { page, pageSize, filters, inputs, setFilter, onPageChange } =
    useTableUrlState({ filters: TABLE_FILTERS });

  // Trees can be large; the flat list is built once per load in the hook and
  // the (cheap) filtering is memoized per filter change here.
  const filteredItems = useMemo(
    () => filterAttentionItems(items, filters),
    [items, filters]
  );

  const isPristine =
    !loading &&
    !error &&
    items.length === 0 &&
    incompletePrograms.length === 0;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader
        title="Necesita atención"
        extra={
          generatedAt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`Actualizado: ${dayjs(generatedAt).format("HH:mm")}`}
            </Text>
          )
        }
      />

      <Space wrap>
        <Select
          allowClear
          placeholder="Tipo de entidad"
          style={{ width: 200 }}
          value={inputs.type || undefined}
          onChange={(value) => setFilter("type", value ?? "")}
          options={TYPE_OPTIONS}
        />
        <Select
          allowClear
          placeholder="Tipo de problema"
          style={{ width: 200 }}
          value={inputs.status || undefined}
          onChange={(value) => setFilter("status", value ?? "")}
          options={STATUS_OPTIONS}
        />
        {!loading && !error && !isPristine && (
          <Text type="secondary">
            {filteredItems.length === 1
              ? "1 ítem necesita atención"
              : `${filteredItems.length} ítems necesitan atención`}
          </Text>
        )}
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la cola de atención."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}

      {!loading && !error && incompletePrograms.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="No se pudo cargar el detalle de algunos programas."
          description={`La lista puede estar incompleta. Programas afectados: ${incompletePrograms.join(
            ", "
          )}.`}
        />
      )}

      {loading && <Skeleton active paragraph={{ rows: 6 }} />}

      {isPristine && (
        <Empty description="Todo el contenido está saludable ✅" />
      )}

      {!loading && !error && !isPristine && (
        <Table<AttentionItem>
          size="small"
          rowKey="key"
          columns={COLUMNS}
          dataSource={filteredItems}
          locale={{ emptyText: "Sin resultados con los filtros aplicados." }}
          pagination={{
            current: page,
            pageSize,
            total: filteredItems.length,
            onChange: onPageChange,
            showSizeChanger: true,
          }}
        />
      )}
    </Space>
  );
};

export default AttentionQueue;
